import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  ganttChartCellCSS,
  ganttChartDefaultCellCSS,
} from './DataTableV2GanttChartCell.sty.js';
import { DataTableV2GanttChartGrid } from './DataTableV2GanttChartGrid.js';
import { useMinMaxFromConfig } from './hooks/useMinMaxFromConfig.js';
import { GanttChartLine } from '../../../../charts/gantt-chart-v2/GanttChartLine.js';
import { type GanttSegmentData } from '../../../../charts/gantt-chart-v2/types/gantt-chart.js';
import { useContainerSize } from '../../../../core/hooks/useContainerSize.js';
import { dataTableMessages } from '../../../DataTableV2/messages.js';
import { DataTableV2DefaultCell } from '../../components/DataTableV2DefaultCell.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import type { DataTableV2GanttColumnConfig } from '../ColumnTypes/ColumnTypes.js';

export const DataTableV2GanttChartCell = ({
  data,
  parentData,
  id,
  config,
}: {
  data: GanttSegmentData | GanttSegmentData[];
  parentData?: GanttSegmentData[];
  id: string;
  config: DataTableV2GanttColumnConfig;
}) => {
  const intl = useIntl();
  const table = useDataTableV2();
  const { min, max } = useMinMaxFromConfig(id);

  const segmentsData = useMemo(
    () => (Array.isArray(data) ? data : [data]),
    [data],
  );
  const parentSegmentsData = useMemo(
    () =>
      parentData === undefined || Array.isArray(parentData)
        ? parentData
        : [parentData],
    [parentData],
  );

  const lineRef = useRef(null);
  const wrapperRef = useRef(null);
  const { width } = useContainerSize(lineRef);
  const { height } = useContainerSize(wrapperRef);

  return (
    <div ref={wrapperRef} className={ganttChartCellCSS}>
      <DataTableV2GanttChartGrid
        width={width}
        height={height}
        min={min}
        max={max}
        config={config}
      />
      <DataTableV2DefaultCell className={ganttChartDefaultCellCSS}>
        <GanttChartLine
          ref={lineRef}
          min={min}
          max={max}
          segmentsData={segmentsData}
          parentSegmentsData={parentSegmentsData}
          size={table.getVisualVariantOptions().rowDensity}
          config={config}
        >
          <GanttChartLine.EmptyState>
            {intl.formatMessage(dataTableMessages.ganttEmtpyState)}
          </GanttChartLine.EmptyState>
          <GanttChartLine.ErrorState>
            {(errorMessage) => (
              <span>
                {intl.formatMessage(dataTableMessages.ganttErrorState)}:{' '}
                {errorMessage}
              </span>
            )}
          </GanttChartLine.ErrorState>
        </GanttChartLine>
      </DataTableV2DefaultCell>
    </div>
  );
};
